import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  describeProcurementInputReason,
  useBulkDeleteProjects,
  useCompareProcurement,
  useMergeProjects,
  useProjectsList,
  type Project,
} from "../api";
import { ProcurementCompareModal } from "../components/ProcurementCompareModal";
import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { PageHeader } from "../components/PageHeader";
import { RefreshAdminData } from "../components/RefreshAdminData";
import { currencyCodeOf, currencySymbol } from "../utils/currency";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import { projectDisplayName, projectSearchName } from "../utils/projectName";
import {
  allOrgs,
  projectMatchesOrgs,
  projectMatchesSearch,
} from "../utils/projectOrgs";
import styles from "./ProjectsRoute.module.css";
import { Chevron } from "../components/Chevron";

const humanReason = (raw: string): string => {
  const input = describeProcurementInputReason(raw);
  if (input) return input;
  if (raw === "no_procurement_assessment_yet")
    return "the procurement assessment hasn't been run yet (open the project and click Run)";
  return raw;
};

interface ProjectRowData {
  project: Project;
}

type SortKey = "name" | "threads" | "files" | "procurement";

type SortDir = "asc" | "desc";

// Default direction when a column is first clicked: numbers + dates
// open largest/most-recent-first (more useful at a glance), text opens
// A-Z. Clicking the active column flips this.
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  threads: "desc",
  files: "desc",
  procurement: "desc",
};

const buildRows = (projects: Project[]): ProjectRowData[] =>
  projects.map((project) => ({ project }));

// Comparable value per sort key. null sorts last regardless of
// direction, so projects with no data for a column don't crowd the top.
const sortValue = (row: ProjectRowData, key: SortKey): string | number | null => {
  const { project } = row;
  switch (key) {
    case "name":
      return projectDisplayName(project).toLowerCase();
    case "threads":
      return project.thread_count ?? null;
    case "files":
      return project.file_count ?? null;
    case "procurement":
      return project.procurement_summary?.adjusted_per_kwp ?? null;
  }
};

const sortRows = (
  rows: ProjectRowData[],
  key: SortKey,
  dir: SortDir,
): ProjectRowData[] => {
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av == null && bv == null) {
      /* fall through to stable tie-break */
    } else if (av == null) {
      return 1;
    } else if (bv == null) {
      return -1;
    } else {
      const res =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      if (res !== 0) return factor * res;
    }
    // Stable tie-break keeps row order deterministic across re-sorts.
    return a.project.project_id.localeCompare(b.project.project_id);
  });
};

export const ProjectsRoute = () => {
  useDocumentTitle("Projects");
  const projectsQuery = useProjectsList();

  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(DEFAULT_DIR[key]);
    }
  };

  const [search, setSearch] = useState("");
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [learningOnly, setLearningOnly] = useState(false);

  const orgOptions = useMemo(
    () => allOrgs(projectsQuery.data ?? []),
    [projectsQuery.data],
  );

  // Drop selected orgs that no longer exist once the list refetches, so
  // a stale filter can't silently hide every project.
  useEffect(() => {
    const valid = new Set(orgOptions.map((o) => o.toLowerCase()));
    setSelectedOrgs((prev) => {
      const next = prev.filter((o) => valid.has(o.toLowerCase()));
      return next.length === prev.length ? prev : next;
    });
  }, [orgOptions]);

  const allRows = useMemo(
    () => buildRows(projectsQuery.data ?? []),
    [projectsQuery.data],
  );

  const scheduledCount = useMemo(
    () => allRows.filter((row) => row.project.learning_agents_enabled === true).length,
    [allRows],
  );

  const rows = useMemo(() => {
    const orgKeys = new Set(selectedOrgs.map((o) => o.toLowerCase()));
    const filtered = allRows.filter(
      (row) =>
        (!learningOnly || row.project.learning_agents_enabled === true) &&
        projectMatchesOrgs(row.project, orgKeys) &&
        projectMatchesSearch(
          projectSearchName(row.project),
          row.project.address,
          search,
        ),
    );
    return sortRows(filtered, sortKey, sortDir);
  }, [allRows, search, selectedOrgs, learningOnly, sortKey, sortDir]);

  const filtersActive =
    search.trim() !== "" || selectedOrgs.length > 0 || learningOnly;
  const clearFilters = () => {
    setSearch("");
    setSelectedOrgs([]);
    setLearningOnly(false);
  };

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const bulkDelete = useBulkDeleteProjects();
  const mergeProjects = useMergeProjects();

  const toggle = (project_id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(project_id)) next.delete(project_id);
      else next.add(project_id);
      return next;
    });
  };

  const allSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.project.project_id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.project.project_id)));
  };

  const clearSelection = () => setSelected(new Set());

  // Procurement figures are only comparable within a single currency (a
  // £/kWp and a €/kWp column can't share a scale, and we don't FX-convert),
  // so the distinct currencies across the selected projects gate the
  // Compare button. Unset currencies fold into GBP (the display default),
  // matching every other surface.
  const selectedCurrencies = useMemo(() => {
    const codes = new Set<string>();
    for (const p of projectsQuery.data ?? []) {
      if (selected.has(p.project_id)) codes.add(currencyCodeOf(p.currency_code));
    }
    return codes;
  }, [projectsQuery.data, selected]);
  const mixedCurrencySelection = selectedCurrencies.size > 1;

  const onBulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    // Show project names (same as the per-row label + single-delete
    // dialog), resolved from the already-loaded list; fall back to the
    // id for legacy rows with no name.
    const nameFor = (id: string) => {
      const project = (projectsQuery.data ?? []).find((p) => p.project_id === id);
      return project ? projectDisplayName(project) : id;
    };
    const msg =
      ids.length === 1
        ? `Delete project "${nameFor(ids[0])}"?\n\nThis removes the project + all runs + every attached file. The action cannot be undone.`
        : `Delete ${ids.length} projects?\n\n${ids.map(nameFor).join("\n")}\n\nThis removes each project + all runs + every attached file. The action cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await bulkDelete.mutateAsync(ids);
      clearSelection();
    } catch (e) {
      window.alert(
        `Delete failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const onMerge = async () => {
    const ids = [...selected];
    if (ids.length < 2) return;
    const primary = window.prompt(
      `Merge ${ids.length} projects into which one (the primary)?\n\n` +
        `Selected:\n${ids.join("\n")}\n\n` +
        `Type the exact project ID of the one to KEEP. The others become history rows on the primary.`,
      ids[0],
    );
    if (!primary) return;
    const trimmed = primary.trim();
    if (!ids.includes(trimmed)) {
      window.alert(
        `"${trimmed}" isn't in the selected list. Pick exactly one of the selected IDs to keep.`,
      );
      return;
    }
    const sources = ids.filter((id) => id !== trimmed);
    if (
      !window.confirm(
        `Merge ${sources.length} project${sources.length === 1 ? "" : "s"} ` +
          `(${sources.join(", ")}) into "${trimmed}"?\n\nRuns + files + uploads ` +
          `from each source move under the primary. The source rows are removed.`,
      )
    )
      return;
    try {
      // Sequential - the BE merges one at a time and each call
      // invalidates the projects list. Concurrent calls would race
      // on the same primary.
      for (const source of sources) {
        await mergeProjects.mutateAsync({
          from_project_id: source,
          to_project_id: trimmed,
        });
      }
      clearSelection();
    } catch (e) {
      window.alert(
        `Merge failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const compareProcurement = useCompareProcurement();
  const [compareError, setCompareError] = useState<string | null>(null);
  const [compareIneligible, setCompareIneligible] = useState<
    Array<{ project_id: string; reason: string }> | null
  >(null);
  const [compareOperationKey, setCompareOperationKey] = useState<string | null>(
    null,
  );
  const onCompareProcurement = async () => {
    const ids = [...selected];
    // Guard the same conditions the button's `disabled` enforces, so a
    // keyboard / programmatic trigger can't start a nonsensical run.
    if (ids.length < 2 || mixedCurrencySelection) return;
    setCompareError(null);
    setCompareIneligible(null);
    try {
      const resp = await compareProcurement.mutateAsync({ project_ids: ids });
      if (resp.status === "ineligible") {
        setCompareIneligible(resp.ineligible_projects);
        return;
      }
      setCompareOperationKey(resp.operation_key);
    } catch (e) {
      setCompareError(
        `Couldn't start the comparison: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
  };

  const loading = projectsQuery.isLoading;
  const selectedCount = selected.size;
  const bulkBusy = bulkDelete.isLoading || mergeProjects.isLoading;

  return (
    <>
      <PageHeader title="Projects" actions={<RefreshAdminData />} />
      <section className={styles.section}>
        {!loading && (allRows.length > 0 || filtersActive) && (
          <div className={styles.controls}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search projects by name or address…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <MultiSelectFilter
              options={orgOptions}
              selected={selectedOrgs}
              onChange={setSelectedOrgs}
            />
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={learningOnly}
                onChange={(e) => setLearningOnly(e.target.checked)}
              />
              Projects included in scheduled runs only - {scheduledCount} of{" "}
              {allRows.length} projects
            </label>
            <span className={styles.resultCount}>
              {filtersActive
                ? `${rows.length} of ${allRows.length} projects`
                : `${allRows.length} project${allRows.length === 1 ? "" : "s"}`}
            </span>
            {filtersActive && (
              <button
                type="button"
                className={styles.clearFilters}
                onClick={clearFilters}
              >
                Clear filters
              </button>
            )}
            {selectedOrgs.length > 0 && (
              <div className={styles.chips}>
                {selectedOrgs.map((org) => (
                  <button
                    key={org}
                    type="button"
                    className={styles.chip}
                    onClick={() =>
                      setSelectedOrgs((prev) =>
                        prev.filter(
                          (o) => o.toLowerCase() !== org.toLowerCase(),
                        ),
                      )
                    }
                    title={`Remove ${org} filter`}
                  >
                    {org}
                    <span className={styles.chipX}>×</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {selectedCount > 0 && (
          <div className={styles.bulkBar}>
            <span className={styles.bulkCount}>
              {selectedCount} selected
            </span>
            <button
              type="button"
              className={styles.bulkClear}
              onClick={clearSelection}
              disabled={bulkBusy}
            >
              Clear
            </button>
            <button
              type="button"
              className={styles.bulkDelete}
              onClick={onBulkDelete}
              disabled={bulkBusy}
            >
              {bulkDelete.isLoading
                ? `Deleting ${selectedCount}...`
                : `Delete ${selectedCount}`}
            </button>
            <button
              type="button"
              className={styles.bulkMerge}
              onClick={onMerge}
              disabled={selectedCount < 2 || bulkBusy}
              title={
                selectedCount < 2
                  ? "Select 2 or more projects to merge"
                  : "Merge selected into one"
              }
            >
              {mergeProjects.isLoading
                ? "Merging..."
                : `Merge ${selectedCount}`}
            </button>
            <button
              type="button"
              className={styles.bulkCompare}
              onClick={onCompareProcurement}
              disabled={selectedCount < 2 || mixedCurrencySelection || bulkBusy}
              title={
                selectedCount < 2
                  ? "Select 2 or more projects to compare"
                  : mixedCurrencySelection
                    ? `Selected projects use different currencies (${[
                        ...selectedCurrencies,
                      ]
                        .map(currencySymbol)
                        .join(" and ")}). Compare projects within one currency.`
                    : "Compare procurement quotes side by side"
              }
            >
              {compareProcurement.isLoading
                ? "Comparing..."
                : `Compare procurement (${selectedCount})`}
            </button>
          </div>
        )}
        {compareError && <div className={styles.compareError}>{compareError}</div>}
        {compareIneligible && (
          <div className={styles.compareError}>
            <strong>Can't compare yet.</strong> These projects don't have everything
            we need:
            <ul>
              {compareIneligible.map((p) => {
                const project = (projectsQuery.data ?? []).find(
                  (pr) => pr.project_id === p.project_id,
                );
                const label = project ? projectDisplayName(project) : p.project_id;
                return (
                  <li key={`${p.project_id}:${p.reason}`}>
                    {label} - {humanReason(p.reason)}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {compareOperationKey && (
          <ProcurementCompareModal
            operationKey={compareOperationKey}
            onClose={() => setCompareOperationKey(null)}
          />
        )}

        {loading ? (
          <div className={styles.note}>Loading projects...</div>
        ) : rows.length === 0 ? (
          <div className={styles.note}>
            {filtersActive
              ? "No projects match the current filters."
              : "No projects yet. Start one with the New project button at the top of the sidebar."}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selectedCount > 0 && selectedCount < rows.length;
                      }
                    }}
                    onChange={toggleAll}
                  />
                </th>
                <SortHeader label="Project" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Threads" sortKey="threads" activeKey={sortKey} dir={sortDir} onSort={onSort} numeric />
                <SortHeader label="Files" sortKey="files" activeKey={sortKey} dir={sortDir} onSort={onSort} numeric />
                <SortHeader label="EPC cost/kWp" sortKey="procurement" activeKey={sortKey} dir={sortDir} onSort={onSort} thClassName={styles.thProcurement} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ProjectRow
                  key={row.project.project_id}
                  row={row}
                  selected={selected.has(row.project.project_id)}
                  onToggle={() => toggle(row.project.project_id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
};

const SortHeader = ({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  numeric,
  thClassName,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  numeric?: boolean;
  thClassName?: string;
}) => {
  const active = activeKey === sortKey;
  return (
    <th
      className={`${styles.th} ${numeric ? styles.thNumeric : ""} ${thClassName ?? ""}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        className={styles.sortButton}
        onClick={() => onSort(sortKey)}
      >
        {label}
        <span className={styles.sortIndicator}>
          {active ? <Chevron open={dir === "asc"} /> : "↕"}
        </span>
      </button>
    </th>
  );
};

// The Threads and Files columns PARTITION what the Files tab counts, and the tab
// now states its total as "N items". Without saying so, a reader compares one
// column against that total and reads a contradiction - the two numbers are
// adjacent on the row and nothing says they add up. Both cells carry this, so
// whichever one is hovered explains the whole row.
const filesTabTotal = (project: {
  file_count?: number;
  thread_count?: number;
}): string => {
  const files = project.file_count ?? 0;
  const threads = project.thread_count ?? 0;
  const part = (n: number, one: string, many: string): string =>
    `${n.toLocaleString("en-GB")} ${n === 1 ? one : many}`;
  return (
    `${part(files, "file", "files")} and ${part(threads, "email thread", "email threads")}` +
    ` - ${part(files + threads, "item", "items")} on the Files tab`
  );
};

const ProjectRow = ({
  row,
  selected,
  onToggle,
}: {
  row: ProjectRowData;
  selected: boolean;
  onToggle: () => void;
}) => {
  const { project } = row;

  return (
    <tr className={`${styles.row} ${selected ? styles.rowSelected : ""}`}>
      <td className={styles.td}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${project.project_id}`}
        />
      </td>
      <td className={styles.td}>
        <Link
          to={`/projects/${encodeURIComponent(project.project_id)}`}
          className={styles.projectLink}
        >
          <div className={styles.projectId}>{projectDisplayName(project)}</div>
          {project.address && (
            <div className={styles.projectAddress}>{project.address}</div>
          )}
        </Link>
      </td>
      <td className={`${styles.td} ${styles.tdNumeric}`}>
        {project.thread_count != null && project.thread_count > 0 ? (
          <Link
            to={`/projects/${encodeURIComponent(project.project_id)}/files`}
            className={styles.fileCountLink}
            onClick={(e) => e.stopPropagation()}
            title={filesTabTotal(project)}
          >
            {project.thread_count}
          </Link>
        ) : (
          "-"
        )}
      </td>
      <td className={`${styles.td} ${styles.tdNumeric}`}>
        {project.file_count != null && project.file_count > 0 ? (
          <Link
            to={`/projects/${encodeURIComponent(project.project_id)}/files`}
            className={styles.fileCountLink}
            onClick={(e) => e.stopPropagation()}
            title={filesTabTotal(project)}
          >
            {project.file_count}
          </Link>
        ) : (
          "-"
        )}
      </td>
      <td className={`${styles.td} ${styles.tdProcurement}`}>
        <ProcurementCell
          summary={project.procurement_summary ?? null}
          currencyCode={project.currency_code}
        />
      </td>
    </tr>
  );
};

// Last-column cell on the projects list: shows the most recent
// procurement assessment's adjusted per-kWp with a colored chip
// matching the same green/amber/red/grey semantics the procurement tab
// uses, in the project's own currency (£ for UK, € for Netherlands - it
// must agree with the Procurement tab). Blank when no assessment has run.
const ProcurementCell = ({
  summary,
  currencyCode,
}: {
  summary: { adjusted_per_kwp: number | null; rag: string | null } | null;
  currencyCode: string | null;
}) => {
  if (!summary || summary.adjusted_per_kwp == null) {
    return <span className={styles.muted}>-</span>;
  }
  const rag = summary.rag ?? "unknown";
  const chipClass =
    rag === "green"
      ? styles.procurementChipGreen
      : rag === "amber"
        ? styles.procurementChipAmber
        : rag === "red"
          ? styles.procurementChipRed
          : styles.procurementChipUnknown;
  const value = `${currencySymbol(currencyCode)}${Math.round(
    summary.adjusted_per_kwp,
  ).toLocaleString("en-GB")}`;
  return <span className={`${styles.procurementChip} ${chipClass}`}>{value}</span>;
};
